import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Router } from '@angular/router';
import { SETTINGS } from '../../../core/config/common.settings';
import { UserService } from '../../../core/services/user.service';
import { EncryptionService } from '../../../core/services/encryption.service';
import { Observable, Subject, tap } from 'rxjs';
import { catchError } from 'rxjs/operators';

@Injectable({
  providedIn: 'root',
})
export class AuthService {
  private loginStatus = new Subject();
  private userId = '';

  constructor(
    private http: HttpClient,
    private encryption: EncryptionService,
    private router: Router,
    private userService: UserService
  ) {}

  postLogin(payload: any): Observable<any> {
    return this.http.post(`${SETTINGS.BASE_API}/user/login`, payload);
  }

  login(authResult: any): void {
    const { access_token, refresh_token, user } = authResult;
    this.setLoggedInUser(JSON.stringify(user));
    this.handleUserNavigation(
      authResult.user.userType,
      authResult.user.role,
      authResult.user.isFirstLogin
    );
    localStorage.setItem(
      SETTINGS.ACCESS_TOKEN,
      this.encryption.encrypt(access_token)
    );
    localStorage.setItem(
      SETTINGS.REFRESH_TOKEN,
      this.encryption.encrypt(refresh_token)
    );
    this.loginStatus.next({ isAuthenticated: true });
    this.userId = user.userId;
  }

  setLoggedInUser(userStr: any): void {
    localStorage.setItem(
      SETTINGS.LOGGED_IN_USER,
      this.encryption.encrypt(userStr)
    );
  }

  getLoggedInUser(): any {
    const userStrEnc: any = localStorage.getItem(SETTINGS.LOGGED_IN_USER);
    return JSON.parse(this.encryption.decrypt(userStrEnc));
  }

  public isLoggedIn(): any {
    if (
      localStorage.getItem(SETTINGS.ACCESS_TOKEN) != null &&
      localStorage.getItem(SETTINGS.REFRESH_TOKEN) != null
    ) {
      const tokenENC: any = localStorage.getItem(SETTINGS.ACCESS_TOKEN);
      try {
        return this.encryption.decrypt(tokenENC);
      } catch (e) {
        this.logout();
      }
    }
    return false;
  }

  getToken(): any {
    if (localStorage.getItem(SETTINGS.ACCESS_TOKEN) != null) {
      const tokenENC: any = localStorage.getItem(SETTINGS.ACCESS_TOKEN);

      try {
        return this.encryption.decrypt(tokenENC);
      } catch (e) {
        return '';
      }
    }
    return false;
  }

  refreshToken(): Observable<any> {
    return this.http
      .post(`${SETTINGS.BASE_API}/user/refresh-token`, {
        refreshToken: this.getRefreshToken(),
        userType: this.getLoggedInUser().userType,
      })
      .pipe(
        tap((res: any) => {
          if (this.getLoggedInUser().userType === SETTINGS.USERS.ADMIN) {
            this.userService.updateUser({
              _id: this.userId,
              lastRefreshedDate: new Date(),
            });
          }
          localStorage.setItem(
            SETTINGS.ACCESS_TOKEN,
            this.encryption.encrypt(res.access_token)
          );
        }),
        catchError((err, caught) => {
          localStorage.clear();
          this.router.navigate(['/auth/login']);
          return err;
        })
      );
  }

  getRefreshToken(): any {
    if (localStorage.getItem(SETTINGS.REFRESH_TOKEN) != null) {
      const refreshTokenENC: any = localStorage.getItem(SETTINGS.REFRESH_TOKEN);

      try {
        return this.encryption.decrypt(refreshTokenENC);
      } catch (e) {
        return null;
      }
    }
    return null;
  }

  handleUserNavigation(userType: string, role: string, isFirstLogin: any) {
    // member login
    if (userType === SETTINGS.USERS.MEMBER) {
      this.router.navigate(['/member']);
    }
    // admin login
    if (userType === SETTINGS.USERS.ADMIN) {
      if (isFirstLogin) {
        this.router.navigate(['admin/users/add-edit', { status: true }]);
      } else {
        if (role === 'USER') {
          this.router.navigate(['/admin/members']);
        } else {
          this.router.navigate(['/admin']);
        }
      }
    }
    // staff login
    if (userType === SETTINGS.USERS.STAFF) {
      this.router.navigate(['/admin/members']);
    }
  }

  logout(): void {
    this.http.post(`${SETTINGS.BASE_API}/user/logout`, {}).subscribe({
      next: () => {
        localStorage.clear();
        this.userService.loggedInUserNameSubject.next('');
        this.userService.loggedInUserProfilePicSubject.next('');
        this.router.navigate(['/auth/login']);
      },
      error: () => {
        localStorage.clear();
        this.userService.loggedInUserNameSubject.next('');
        this.userService.loggedInUserProfilePicSubject.next('');
        this.router.navigate(['/auth/login']);
      },
    });
  }

  changePwd(payload: any): Promise<any> {
    return new Promise((resolve, reject) => {
      this.http
        .post(`${SETTINGS.BASE_API}/admin/change-password`, payload)
        .subscribe({
          next: (response: any) => {
            resolve(response);
          },
          error: (error: any) => {
            reject(error.error);
            // this.notificationService.error('Error', error.error.message);
          },
        });
    });
  }
}
